import { Point } from "pixi.js";

export class WayAnalyzer {
    /**
     * Computes all the win ways/chains/paths from a given win symbols.
     * 
     * This is useful if you wish to show the ways one by one, using a multi-line particle in example.
     * @param winSymbolPositions The winning symbol positions, whose ways you wish to detect.
     * @returns The computed ways. Each way would contain properly sorted points.
     */
    public static computeWays(winSymbolPositions: Point[]): Point[][] {
        // Sorting the symbol positions
        winSymbolPositions.sort((a, b) => {
            if (a.x == b.x) {
                return a.y - b.y;
            }

            return a.x - b.x;
        });

        // Creating ways. Each way goes from left to right
        // and it starts from a pivot point.
        // In case we have more than 1 symbol on the same column,
        // we should compute a new way for it (again starting from the pivot).
        let ways = new Array<Array<Point>>();

        // Finding the pivot points.
        for (let i = 0; i < winSymbolPositions.length; i++) {
            const pivotPoint = winSymbolPositions[i];
            if (winSymbolPositions.find(p => p.x < pivotPoint.x)) {
                // This symbol is not the first one of the way,
                // so it can not be a pivot.
                continue;
            }

            // Checking if we have more than 1 neighbour at the same x position.
            // If so, we should create a new way for it.
            let neighbours = this.getNeighbours(pivotPoint, winSymbolPositions, true);
            let splitWays = this.splitWays(pivotPoint, neighbours);
            for (let index = 0; index < splitWays.length; index++) {
                const way = splitWays[index];
                ways.push(way);
            }
        }

        return ways;
    }

    /**
     * Gets the neighbours of a given pivot position at the provided (grid of) points.
     * @param point The pivot point, whose neighbours to find.
     * @param points The points (grid), where to search neighbours for.
     * @param onTheRightSide Pass true to search on the right side of the pivot.
     * Pass false for the left side neighbours.
     * @returns All the neighbours on the specified side.
     */
    private static getNeighbours(point: Point, points: Point[], onTheRightSide: boolean): Point[] {
        let neighbours = new Array<Point>();
        if (points.length == 0) {
            return neighbours;
        }

        for (let i = 0; i < points.length; i++) {
            const currentPoint = points[i];
            let condition = onTheRightSide ? currentPoint.x > point.x : currentPoint.x < point.x;
            if (condition) {
                neighbours.push(currentPoint);
                // Getting also the neighbours of the neighbours.
                let nons = this.getNeighbours(currentPoint, neighbours, onTheRightSide);
                for (let j = 0; j < nons.length; j++) {
                    const non = nons[j];
                    neighbours.push(non);
                }
            }
        }

        return neighbours;
    }

    /**
     * Gets all the possible ways from a given point, by providing its right side neighbours.
     * @param point The pivot (start) point. This point must not be at the middle of the way/chain.
     * @param neighbours The neighbours on the right side of the pivot point.
     * @returns All the possible ways, which starts from the pivot.
     */
    private static splitWays(point: Point, neighbours: Point[]): Array<Array<Point>> {
        let ways = new Array<Array<Point>>();
        if (neighbours.length == 0) {
            return ways;
        }

        // Here we keep all the intercepted ways (semi-ways).
        let chains = new Array<Array<Point>>();

        // Here we keep the current way points.
        let currentWay = new Array<Point>();
        currentWay.push(point);

        // Analyzing the possible ways
        for (let i = 0; i < neighbours.length; i++) {
            const neighbour = neighbours[i];
            currentWay.push(neighbour);

            while (i < neighbours.length - 1) {
                let next = neighbours[i + 1];
                // Checking whether the next win symol is on the same column.
                if (neighbour.x == next.x) {
                    i++;
                    if (chains.length > 0) {
                        let lastChain = chains[chains.length - 1];
                        if (lastChain[lastChain.length - 1].y <= neighbour.y) {
                            // Skipping until we reach unvisited cell.
                            continue;
                        }
                    }

                    // Ok we found where the way intercepts.
                    let chain = new Array<Point>();

                    // Adding the visited points so far (those on the left side),
                    // so we can process them at later stage.
                    // We do not need the last point in the current way.
                    // Instead we would need the 'next' point (see next comment).
                    for (let j = 0; j < currentWay.length - 1; j++) {
                        const pointInChain = currentWay[j];
                        chain.push(pointInChain);
                    }

                    // Pushing the next, because it will be our pivot point.
                    // In case the way ends here, we are fine.
                    // Otherwise 'next' point must be excluded from
                    // the final path to avoid repeated symbol pos.
                    chain.push(next);
                    chains.push(chain);
                }
                else {
                    break;
                }
            }
        }

        ways.push(currentWay);

        // Processing the chains (semi-ways) recursively
        for (let i = 0; i < chains.length; i++) {
            const chain = chains[i];
            let lastPoint = chain[chain.length - 1];
            // Checking whether we reached the end of the chain or not.
            // Passing the neighours, regarding the chain's last point.
            let splitWays = this.splitWays(lastPoint, this.getNeighbours(lastPoint, neighbours, true));
            if (splitWays.length == 0) {
                // No further ways detected, which means we
                // will simply add the chain as is.
                ways.push(chain);
            } else {
                // The chain continues ahead. We already have its first part (left side).
                // Getting its second part (right side).
                for (let j = 0; j < splitWays.length; j++) {
                    const rightSide = splitWays[j];
                    let way = new Array<Point>();
    
                    // Pushing the left side symbols (except the last one)
                    // in order to avoid repeated positions.
                    for (let k = 0; k < chain.length - 1; k++) {
                        const leftSidePoint = chain[k];
                        way.push(leftSidePoint);
                    }
    
                    // Pushing the right side symbols
                    for (let l = 0; l < rightSide.length; l++) {
                        const rightSidePoint = rightSide[l];
                        way.push(rightSidePoint);
                    }
    
                    // Our way is complete.
                    ways.push(way);
                }
            }
        }

        return ways;
    }
}

export default new WayAnalyzer();